/**
 * SirusFrame — R232q20 Phase 1 Foundation.
 *
 * Reusable panel chrome for the Sirus 3.3.5a-style HUD. Wraps any panel
 * content with a thin gold-edged border, glass-card backdrop, and four
 * SVG filigree corners (top-left original, others mirrored on X/Y).
 *
 * Visual reference: the `CornerOrnament` filigree used on RealmSelect.tsx,
 * promoted here so every Sirus-themed panel renders identical chrome.
 *
 * Sizes adjust the corner-ornament size + inner padding, NOT the frame's
 * width — that is left to the caller's `style` / `className`. So a
 * `<SirusFrame size="sm" style={{ width: 320 }}>…</SirusFrame>` keeps the
 * frame at 320px, just with smaller corner trim.
 */

import type { CSSProperties, ReactNode } from 'react';

export type SirusFrameSize = 'sm' | 'md' | 'lg';

interface SirusFrameProps {
  size?: SirusFrameSize;
  /** Adds a soft gold halo behind the frame (cast bar / boss frame). */
  glow?: boolean;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

const ORNAMENT_PX: Record<SirusFrameSize, number> = {
  sm: 36,
  md: 56,
  lg: 80,
};

const PADDING_PX: Record<SirusFrameSize, number> = {
  sm: 12,
  md: 18,
  lg: 26,
};

/**
 * Single SVG corner ornament. The same path is rendered four times, with
 * scaleX/scaleY transforms used to mirror it into the other three corners.
 * Path is anchored at (0,0) so the SVG itself sits flush in the corner.
 */
function CornerOrnament({
  flipX = false,
  flipY = false,
  size = 56,
}: {
  flipX?: boolean;
  flipY?: boolean;
  size?: number;
}) {
  const transform = `${flipX ? 'scaleX(-1) ' : ''}${flipY ? 'scaleY(-1) ' : ''}`.trim();
  return (
    <svg
      viewBox="0 0 96 96"
      width={size}
      height={size}
      style={{
        transform: transform || undefined,
        transformOrigin: 'center',
        position: 'absolute',
        pointerEvents: 'none',
      }}
      aria-hidden
    >
      <defs>
        <linearGradient id="aiwariSirusGold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#8A6B2E" />
          <stop offset="50%" stopColor="#E8C77B" />
          <stop offset="100%" stopColor="#8A6B2E" />
        </linearGradient>
      </defs>
      <g
        fill="none"
        stroke="url(#aiwariSirusGold)"
        strokeWidth="1.4"
        strokeLinecap="round"
      >
        <path d="M0 1.5 L72 1.5" />
        <path d="M1.5 0 L1.5 72" />
        <path d="M1.5 24 L24 24 L24 1.5" opacity="0.65" />
        <path d="M10 10 Q22 10 22 22 Q22 34 10 34" />
        <circle cx="6" cy="6" r="1.3" fill="#E8C77B" stroke="none" />
        <circle cx="22" cy="22" r="1.1" fill="#C9A96A" stroke="none" />
      </g>
    </svg>
  );
}

export function SirusFrame({
  size = 'md',
  glow = false,
  className,
  style,
  children,
}: SirusFrameProps) {
  const ornamentSize = ORNAMENT_PX[size];
  const pad = PADDING_PX[size];

  // Corners are positioned absolutely outside the inner content padding so
  // they "bite" the panel edge. Negative offsets pull them in by 2px so
  // the filigree's outer rim aligns with the 1.5px stroke we draw inside.
  const cornerStyle: CSSProperties = {
    position: 'absolute',
    width: ornamentSize,
    height: ornamentSize,
  };

  const rootClass = ['aiwari-frame', className].filter(Boolean).join(' ');

  return (
    <div
      className={rootClass}
      data-glow={glow ? 'true' : undefined}
      style={{
        padding: pad,
        ...style,
      }}
    >
      {/* Top-left */}
      <div style={{ ...cornerStyle, top: -2, left: -2 }}>
        <CornerOrnament size={ornamentSize} />
      </div>
      {/* Top-right */}
      <div style={{ ...cornerStyle, top: -2, right: -2 }}>
        <CornerOrnament size={ornamentSize} flipX />
      </div>
      {/* Bottom-left */}
      <div style={{ ...cornerStyle, bottom: -2, left: -2 }}>
        <CornerOrnament size={ornamentSize} flipY />
      </div>
      {/* Bottom-right */}
      <div style={{ ...cornerStyle, bottom: -2, right: -2 }}>
        <CornerOrnament size={ornamentSize} flipX flipY />
      </div>
      {children}
    </div>
  );
}
